import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCart } from '../context/CartContext';
import { soundEffects } from '../utils/soundEffects';
import ProductCard from '../components/ProductCard';
import Products2Hero from '../components/Products2Hero';
import { generateProductSlug } from '../utils/seoUtils';
import './LaptopsPage.css';

const AccessoriesPage = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [accessories, setAccessories] = useState([]);
  const [filteredAccessories, setFilteredAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('featured');

  useEffect(() => {
    fetchAccessories();
  }, []);

  useEffect(() => {
    applySorting();
  }, [accessories, sortBy]);

  const fetchAccessories = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'products', 'accessories', 'items'));
      const accessoryData = querySnapshot.docs.map((docSnapshot) => {
        const data = docSnapshot.data();
        const id = docSnapshot.id;
        const slug = generateProductSlug({ id, ...data });
        
        if (!data.slug || data.slug !== slug) {
          updateDoc(doc(db, 'products', 'accessories', 'items', id), { slug })
            .catch(err => console.warn('Accessory slug update failed:', err));
        }
        
        return { id, ...data, slug };
      });
      setAccessories(accessoryData);
    } catch (err) {
      setError('Failed to load accessories');
      console.error('Error fetching accessories:', err);
    } finally {
      setLoading(false);
    }
  };

  const applySorting = () => {
    let sorted = [...accessories];

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'discount':
        sorted.sort((a, b) => (b.discount || 0) - (a.discount || 0));
        break;
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        // Featured - keep original order
        break;
    }

    setFilteredAccessories(sorted);
  };

  const handleProductClick = (accessory) => {
    const slug = generateProductSlug(accessory);
    navigate(`/accessory/${slug || accessory.id}`);
  };

  const handleAddToCart = (product) => {
    const cartItem = {
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      originalPrice: product.originalPrice,
      imageUrls: product.imageUrls,
      features: [product.model, product.type].filter(Boolean),
      quantity: 1
    };

    addToCart(cartItem);
    soundEffects.playAddToCartSound();
  };

  return (
    <div className="laptops-page">
      <Products2Hero
        className="hero-tight"
        tagline="PROFESSIONAL GRADE · CERTIFIED REFURBISHED · QUALITY GUARANTEED"
        title1="Accessories"
        title2=""
        title3=""
        description=""
        button1Link="#laptops-collection"
      />
      <div className="container" id="laptops-collection">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading accessories...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
          </div>
        ) : (
          <>
            {/* Sort Section */}
            <div className="filter-section">
              <div className="filter-header">
                <div className="results-info">
                  <h2>Showing {filteredAccessories.length} accessories</h2>
                  <p>Find the perfect accessories for your devices</p>
                </div>
                <div className="filter-controls">
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="featured">Featured</option>
                    <option value="name">Name: A to Z</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="newest">Newest First</option>
                    <option value="discount">Best Deals</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="laptops-grid">
              {filteredAccessories.map((accessory) => (
                <div key={accessory.id} className="laptop-card-wrapper">
                  <ProductCard
                    product={{
                      image: accessory.imageUrls?.[0] || '/api/placeholder/300/200',
                      imageAlt: accessory.name,
                      badge: accessory.availability === 'Available' ? 'In Stock' : 'Out of Stock',
                      isNew: false,
                      stockLeft: accessory.stockLeft,
                      brand: accessory.brand || 'NexaWave',
                      name: accessory.name,
                      subtitle: [accessory.condition || 'New', accessory.warranty || '6 Months'].filter(Boolean).join(' | '),
                      scentNote: [accessory.model, accessory.type || 'Accessory'].filter(Boolean).join(' | '),
                      rating: Number(accessory.rating) || 4.8,
                      reviews: Number(accessory.reviews) || 120,
                      originalPrice: Number(accessory.originalPrice) || Number(accessory.price) || 0,
                      salePrice: Number(accessory.price) || Number(accessory.originalPrice) || 0
                    }}
                    onCardClick={() => handleProductClick(accessory)}
                    onAddToCart={() => handleAddToCart(accessory)}
                    canAddToCart={accessory.availability === 'Available'}
                    showRating={false}
                    titleTwoLine={true}
                  />
                </div>
              ))}
            </div>

            {filteredAccessories.length === 0 && (
              <div className="no-results">
                <div className="no-results-icon">🔍</div>
                <h3>No accessories found</h3>
                <p>Check back soon for more accessories</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AccessoriesPage;
